import { forwardRef, useEffect, useState } from "react";
import styled, { css } from "styled-components";
import { RootState } from "../../store";
import { useSelector } from "react-redux";
import { getFooterLabels } from "../../utils/getLabels";
import { useLocation } from "react-router-dom";

const FOOTER_ROUTES = [
	"/",
	"/login",
	"/register",
	"/programs",
	"/edit-entry",
	"/media",
	"/credits/company",
	"/credits/individual",
	"/tags",
	"/user-info",
	"/company-info",
	"/entries/draft-entries",
	"/entries/entries-search",
	"/entries/orders",
	// exclude cart and payments as the footer is built into the payment drawer
	// "/entries/cart",
	// "/payment",
];

const StyledFooter = styled.footer`
	${({ theme }) => css`
		width: 100%;
		padding: 1rem;
		font-size: ${theme.xxSmallSize};
		background: ${theme.colorBackgroundLight};
		margin-top: auto;
		text-align: center;

		.row-1 {
			display: flex;
			justify-content: center;
			column-gap: 1rem;
			row-gap: 0.25rem;
			flex-wrap: wrap;
			margin-bottom: 0.25rem;

			a {
				text-decoration: none;
			}
		}

		.row-2 {
			display: flex;
			justify-content: center;
			column-gap: 1rem;
			row-gap: 0.25rem;
			flex-wrap: wrap;
			width: fit-content;
			margin: 0 auto;

			a {
				text-decoration: none;
			}
		}
	`}
`;

export const Footer = forwardRef<HTMLElement, { className?: string }>(
	({ className }, ref) => {
		const { settings } = useSelector((state: RootState) => state.settings);
		const [footerLabels, setFooterLabels] = useState<Record<
			string,
			string
		> | null>(null);

		useEffect(() => {
			if (settings.featureFlags["Footer"]) {
				getFooterLabels().then((labels) => setFooterLabels(labels));
			} else setFooterLabels(null);
		}, [settings]);

		return settings.featureFlags["Footer"] && footerLabels ? (
			<StyledFooter className={className ?? ""} ref={ref}>
				<div className="row-1">
					{footerLabels?.footerAboutUs && (
						<a
							href={footerLabels.footerAboutUs}
							target="_blank"
							rel="noopener noreferrer"
						>
							About Us
						</a>
					)}
					{footerLabels?.footerTermsAndConditions && (
						<a
							href={footerLabels.footerTermsAndConditions}
							target="_blank"
							rel="noopener noreferrer"
						>
							Terms and Conditions
						</a>
					)}
					{footerLabels.footerPrivacyPolicy && (
						<a
							href={footerLabels.footerPrivacyPolicy}
							target="_blank"
							rel="noopener noreferrer"
						>
							Privacy Policy
						</a>
					)}
					{footerLabels.footerPricingPolicy && (
						<a
							href={footerLabels.footerPricingPolicy}
							target="_blank"
							rel="noopener noreferrer"
						>
							Pricing Policy
						</a>
					)}
					{footerLabels.footerContactUs && (
						<a
							href={footerLabels.footerContactUs}
							target="_blank"
							rel="noopener noreferrer"
						>
							Contact Us
						</a>
					)}
					{footerLabels.footerDisclaimer && (
						<a
							href={footerLabels.footerDisclaimer}
							target="_blank"
							rel="noopener noreferrer"
						>
							Disclaimer
						</a>
					)}
					{footerLabels.footerRefundPolicy && (
						<a
							href={footerLabels.footerRefundPolicy}
							target="_blank"
							rel="noopener noreferrer"
						>
							Refund Policy
						</a>
					)}
				</div>
				<div className="row-2">
					{footerLabels.footerCopyright && (
						<span>© {footerLabels.footerCopyright}</span>
					)}
					{footerLabels.footerBranding && footerLabels.footerBrandingUrl && (
						<a
							href={footerLabels.footerBrandingUrl}
							target="_blank"
							rel="noopener noreferrer"
						>
							{footerLabels.footerBranding}
						</a>
					)}
				</div>
			</StyledFooter>
		) : null;
	},
);

const FooterWrapper = ({ children }: { children: React.ReactNode }) => {
	const location = useLocation();
	const showFooter = FOOTER_ROUTES.some(
		(route) =>
			location.pathname === route ||
			location.pathname.startsWith("/programs/") ||
			location.pathname.startsWith("/edit-entry/"),
	);

	if (!showFooter) {
		return <>{children}</>;
	}

	return (
		<div className="flex flex-col w-full h-full">
			{children}
			<Footer />
		</div>
	);
};

export default FooterWrapper;
